import React, { useState, useRef } from 'react';
import { Word, WordWithStatus, searchWords, addWordToCategory, WordStatus } from '../utils/api';
import WordCard from './WordCard';
import '../styles/AddWordModal.scss';
import logger from '../utils/logger';

interface AddWordModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryName: string;
  onWordAdded: () => void;
}
const AddWordModal: React.FC<AddWordModalProps> = ({
  isOpen,
  onClose,
  categoryName,
  onWordAdded
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<WordWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedWord, setSelectedWord] = useState<Word | null>(null);
  const [addedWords, setAddedWords] = useState<Set<string>>(new Set());
  
  const clickTimer = useRef<NodeJS.Timeout | null>(null);
  const clickCount = useRef(0);

  logger.log('Рендер AddWordModal:', {
    isOpen,
    categoryName,
    searchResults: searchResults.length,
    isLoading,
    error
  });
  React.useEffect(() => {
    if (isOpen) {
      logger.log('Модальное окно открыто для категории:', categoryName);
      setInputValue('');
      setSearchResults([]);
      setError(null);
      setAddedWords(new Set());
    }
  }, [isOpen, categoryName]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  const handleSearch = async () => {
    if (!inputValue.trim()) {
      setError('Введите слово для поиска');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSearchResults([]); 
    try {
      const response = await searchWords(inputValue.trim());
      if (response.success && response.data) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          if ('word' in response.data[0]) {
            const adaptedData = response.data.map((item: any) => {
              if (item.word && typeof item.word === 'object' && item.status && item.categories) {
                return item as WordWithStatus;
              } 
              else {
                return {
                  word: {
                    word: item.word || '',
                    pinyin: item.pinyin || '',
                    translation: item.translation || '',
                    priority: item.priority || 0,
                    example: item.example || null
                  },
                  status: 'IN_PROGRESS' as WordStatus,
                  categories: []
                } as WordWithStatus;
              }
            });
            setSearchResults(adaptedData);
          }
        } else {
          setError('Слова не найдены. Попробуйте другой запрос.');
        }
      }
    } catch (err) {
      // Не отображаем ошибку пользователю
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  const handleAddWord = async (word: Word | any, shouldCloseModal: boolean = true) => {
    let wordToAdd: Word;
    if (typeof word.word === 'string') {
      wordToAdd = word;
    } else if (typeof word.word === 'object') {
      wordToAdd = word.word;
    } else {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await addWordToCategory(wordToAdd, categoryName);
      if (response.success) {
        if (shouldCloseModal) {
          onClose();
          setTimeout(() => {
            onWordAdded();
          }, 300);
        } else {
          setAddedWords(prev => {
            const newSet = new Set(prev);
            newSet.add(wordToAdd.word);
            return newSet;
          });
          onWordAdded();
        }
      }
    } catch (err) {
      // Не отображаем ошибку пользователю
    } finally {
      setIsLoading(false);
    }
  };
  const handleSearchResultClick = (item: WordWithStatus, e: React.MouseEvent) => {
    clickCount.current += 1;
    
    if (clickCount.current === 1) {
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
      }
      
      clickTimer.current = setTimeout(() => {
        if (clickCount.current === 1) {
          logger.log('Нажатие на слово:', item.word);
          setSelectedWord(item.word);
        }
        clickCount.current = 0;
        clickTimer.current = null;
      }, 300);
    } else if (clickCount.current === 2) {
      clickCount.current = 0;
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
        clickTimer.current = null;
      }
      
      handleAddWord(item, false);
    }
  };
  const closeWordCard = () => {
    setSelectedWord(null);
  };
  if (!isOpen) return null;
  return (
    <div className="add-word-modal-overlay" onClick={onClose}>
      <div className="add-word-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Добавить слово в категорию "{categoryName}"</h2>
        <div className="search-input-container">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Введите слово для поиска..."
            className="search-input"
            disabled={isLoading}
            autoFocus
          />
          <button 
            className="search-button"
            onClick={handleSearch}
            disabled={isLoading}
          >
            {isLoading ? 'Поиск...' : 'Найти'}
          </button>
        </div>
        {isLoading && (
          <div className="loading-message">
            Выполняется поиск слов...
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
        {searchResults.length > 0 && (
          <div className="search-results">
            <h3>Найденные слова: {searchResults.length}</h3>
            <div className="words-list">
              {searchResults.map((item, index) => (
                <div 
                  key={`${item.word.word}-${index}`}
                  className={`word-item ${addedWords.has(item.word.word) ? 'added' : ''}`}
                  onClick={(e) => handleSearchResultClick(item, e)}
                >
                  <div className="word">{item.word.word}</div>
                  <div className="pinyin">{item.word.pinyin || 'Нет пиньиня'}</div>
                  <div className="translation">{item.word.translation || 'Нет перевода'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {searchResults.length === 0 && !isLoading && !error && (
          <div className="no-results-message">
            Введите текст для поиска слов
          </div>
        )}
        <div className="modal-footer">
          <button className="close-button" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
      {selectedWord && (
        <WordCard 
          word={selectedWord}
          onClose={closeWordCard}
          onAddToCategory={() => {
            handleAddWord({ word: selectedWord }, true);
            closeWordCard();
          }}
          categoryName={categoryName}
          isInCategory={false}
        />
      )}
    </div>
  );
};
export default AddWordModal; 